/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Item } from 'paper';
import { flatMap } from 'lodash';
import { arrayify } from '@gh/core-util';

export interface PaperElementChanges {
  changedProperties: string[];

  hasChanged(propName: string): boolean;

  getPreviousValue(propName: string): any;

  getCurrentValue(propName: string): any;
}

function wrapAngularChanges(context: any, propNames: string[], changes: SimpleChanges): PaperElementChanges {
  const changedProperties = propNames.filter((name) => Object.prototype.hasOwnProperty.call(changes, name));
  return {
    changedProperties,
    hasChanged(propName: string): boolean {
      return Object.prototype.hasOwnProperty.call(changes, propName);
    },
    getCurrentValue(propName: string): any {
      const change = changes[propName];
      return change ? change.currentValue : context[propName];
    },
    getPreviousValue(propName: string): any {
      const change = changes[propName];
      return change ? changes[propName].previousValue : context[propName];
    },
  };
}

export interface HasPaperItem {
  item: Item;
}

export type PaperElementEffectFn = (item: Item, currentValues: any[], previousValues: any[]) => void;

export interface PaperElementEffect {
  fields: string[];
  effect: PaperElementEffectFn;
}

export const paperEffect = (fields: string[] | string, effect: PaperElementEffectFn): PaperElementEffect =>
  ({ fields: <string[]>arrayify(fields), effect });

export abstract class GraphicalTakeOffElementBase implements OnInit, OnDestroy, OnChanges {
  private _initChanges?: PaperElementChanges;
  private _firstChange = true;
  private _item: Item;
  private _propNames: string[];

  set item(_: Item) {
    throw new Error('Use initPaperItem instead');
  }

  get item(): Item {
    return this._item;
  }

  constructor(private _effects: PaperElementEffect[]) {
    this._propNames = flatMap(_effects, (effect) => effect.fields);
  }

  ngOnInit(): void {
    this._item = this.createPaperItem();
    this.applyFirstPaperChanges();
  }

  ngOnDestroy(): void {
    this._item.remove();
    this._item = <any>void 0;
  }

  ngOnChanges(changes: SimpleChanges): void {
    const paperChanges = wrapAngularChanges(this, this._propNames, changes);
    if (this._firstChange) {
      this._initChanges = paperChanges;
      this._firstChange = false;
    } else {
      this.applyPaperChanges(paperChanges);
    }
  }

  protected createPaperItem(): Item {
    throw new Error('createPaperItem is not implemented');
  }

  private applyFirstPaperChanges(): void {
    if (this._initChanges) {
      this.applyPaperChanges(this._initChanges);
    }
  }

  private applyPaperChanges(changes: PaperElementChanges): void {
    this._effects.forEach((effect) => {
      if (effect) {
        const isChanged = effect.fields.some((field) => changes.hasChanged(field));
        if (isChanged) {
          effect.effect(
            this.item,
            effect.fields.map((field) => changes.getCurrentValue(field)),
            effect.fields.map((field) => changes.getPreviousValue(field)));
        }
      }
    });
  }
}
